import React, { useCallback, useMemo } from 'react';

import { useSnackbar } from 'notistack';

import {
  Button,
  IconButton,
  CloseIcon,
  makeStyles,
  darken,
  lighten,
} from '@gogame/components';

const getColor = (mode, color, coefficient = 0.21) => {
  if (mode === 'dark') {
    return lighten(color, coefficient);
  } else {
    return darken(color, coefficient);
  }
};

const useStyles = makeStyles(
  (theme) => {
    const successColor = getColor(
      theme.palette.mode,
      theme.palette.success.main
    );
    const errorColor = getColor(theme.palette.mode, theme.palette.error.main);
    const warningColor = getColor(
      theme.palette.mode,
      theme.palette.warning.main
    );
    const infoColor = getColor(
      theme.palette.mode,
      theme.palette.info.main,
      0.21 //for contrast text
    );
    const defaultColor = getColor(
      theme.palette.mode,
      theme.palette.mode === 'light'
        ? theme.palette.grey[900]
        : theme.palette.common.white
    );

    return {
      //
      root: {
        ...theme.typography.body2,
        '& #notistack-snackbar': {
          maxWidth: 800,
          wordBreak: 'break-word',
        },
        '& a': {
          color: 'inherit',
          textDecoration: 'underline',
        },
        '&.notistack-MuiContent-success': {
          color: theme.palette.getContrastText(successColor),
          backgroundColor: successColor,
        },
        '&.notistack-MuiContent-error': {
          color: theme.palette.getContrastText(errorColor),
          backgroundColor: errorColor,
        },
        '&.notistack-MuiContent-warning': {
          color: theme.palette.getContrastText(warningColor),
          backgroundColor: warningColor,
        },
        '&.notistack-MuiContent-default': {
          color: theme.palette.getContrastText(defaultColor),
          backgroundColor: defaultColor,
        },
        '&.notistack-MuiContent-info': {
          color: theme.palette.getContrastText(infoColor),
          backgroundColor: infoColor,
        },
      },
    };
  },
  { name: 'ScreenMessage' }
);

const visibleSnckbarsMap = new Map();

export const useScreenMessage = (id) => {
  const { enqueueSnackbar, closeSnackbar, updateSnackbar } = useSnackbar();
  const classes = useStyles();

  const enqueueMessage = useCallback(
    (
      message,
      {
        ttl,
        closable,
        callback,
        callbackTextColor = 'inherit',
        callbackText = 'open',
        variant,
        multiple = false,
        multipleTitle,
        onClose,
        ...other
      } = {}
    ) => {
      const { ['data-cy']: dataCy, ...options } = other;

      let prevMessageKey = undefined;
      if (!options.preventDuplicate && id) {
        prevMessageKey = visibleSnckbarsMap.get(id);
      }

      const action = (key) =>
        (closable || callback) && (
          <React.Fragment>
            {callback ? (
              <Button
                color={callbackTextColor}
                onClick={(e) => {
                  closeSnackbar(key);
                  callback(e);
                }}
              >
                {callbackText}
              </Button>
            ) : null}
            {closable ? (
              <IconButton
                color={'inherit'}
                icon={<CloseIcon />}
                onClick={() => closeSnackbar(key)}
              >
                <CloseIcon fontSize={'small'} />
              </IconButton>
            ) : null}
          </React.Fragment>
        );

      const key = enqueueSnackbar({
        ...(multiple
          ? {
              variant: 'multiple',
              messageVariant: variant,
              title: multipleTitle || `${message?.length} Messages`,
              onCloseMessage: (id) => closeSnackbar(id),
              messages: message,
            }
          : {
              variant,
              message,
            }),
        persist: !ttl,
        autoHideDuration: ttl,
        onEntered: () => {
          prevMessageKey && closeSnackbar(prevMessageKey);
        },
        onClose: (e, reason) => {
          if (visibleSnckbarsMap.get(id) === key) {
            visibleSnckbarsMap.delete(id);
          }

          if (onClose) {
            onClose(e, reason);
          }
        },
        SnackbarProps: {
          'data-cy': dataCy,
        },
        className: classes.root,
        action: (snackKey) => action(snackKey),
        ...options,
      });

      if (id) {
        visibleSnckbarsMap.set(id, key);
      }

      return {
        dismiss: () => closeSnackbar(key),
        update: (newMessage, { ttl } = {}) =>
          updateSnackbar(key, newMessage, { autoHideDuration: ttl }),
      };
    },
    [classes.root, closeSnackbar, enqueueSnackbar, id, updateSnackbar]
  );

  const showErrorMessage = useCallback(
    (
      message,
      { ttl, closable = true, callback, multiple, ...options } = {}
    ) => {
      return enqueueMessage(message, {
        ttl,
        preventDuplicate: true,
        variant: 'error',
        multipleTitle: `${message?.length} Errors`,
        closable,
        callback,
        multiple,
        'data-cy': 'errorMessage',
        ...options,
      });
    },
    [enqueueMessage]
  );

  const showWarningMessage = useCallback(
    (message, { ttl, closable = true, ...options } = {}) => {
      return enqueueMessage(message, {
        ttl,
        variant: 'warning',
        multipleTitle: `${message?.length} Warnings`,
        closable,
        'data-cy': 'warningMessage',
        ...options,
      });
    },
    [enqueueMessage]
  );

  const showInfoMessage = useCallback(
    (message, { ttl = 3000, closable = false, ...options } = {}) => {
      return enqueueMessage(message, {
        ttl,
        variant: 'info',
        closable,
        'data-cy': 'infoMessage',
        ...options,
      });
    },
    [enqueueMessage]
  );

  const showSuccessMessage = useCallback(
    (message, { ttl = 3000, closable = false, ...options } = {}) => {
      return enqueueMessage(message, {
        ttl,
        variant: 'success',
        closable,
        'data-cy': 'successMessage',
        ...options,
      });
    },
    [enqueueMessage]
  );

  const showMessage = useCallback(
    (message, { ttl = 3000, closable = false, ...options } = {}) => {
      return enqueueMessage(message, {
        ttl,
        closable,
        'data-cy': 'infoMessage',
        ...options,
      });
    },
    [enqueueMessage]
  );

  return useMemo(
    () => ({
      showMessage,
      showInfoMessage,
      showErrorMessage,
      showSuccessMessage,
      showWarningMessage,
    }),
    [
      showMessage,
      showInfoMessage,
      showErrorMessage,
      showSuccessMessage,
      showWarningMessage,
    ]
  );
};
